import React, { useMemo, useState } from "react";
import { useAppDispatch } from "store";
import { Form, DateRangePicker, Input, HStack } from "rsuite";
import { TagFilter } from "@rsuite/icons";
import { validationHasErrors, formatDateToLocalISOString } from "utils";
import styled from "styled-components";
import debounce from "lodash/debounce";

import FormControl from "components/base/FormControl";
import FormGroup from "components/base/FormGroup";
import Panel from "components/base/Panel";

import { DumpFilters, dumpFormModel } from "types/filterConfig";
import { parseUTC, displayLocalDateToBackendUTCString } from "utils/time";


type ExtractedDumpsFiltersProps = {
    expanded?: boolean;
    model: typeof dumpFormModel;
    loading: boolean;
    filters: Record<string, any>;
    formError: Record<string, string>;
    searchParams: URLSearchParams;
    notUpdatableKeys: string[];
    currentParamsRef: React.RefObject<URLSearchParams>;
    setFilters: React.Dispatch<React.SetStateAction<DumpFilters>>;
    setRefreshKey: (key: number) => void;
    setFormError: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setSearchParams: (params: URLSearchParams) => void;
};


const ExtractedDumpsFilters: React.FC<ExtractedDumpsFiltersProps> = ({
    expanded,
    model,
    loading,
    filters,
    formError,
    searchParams,
    notUpdatableKeys,
    currentParamsRef,
    setFilters,
    setFormError,
    setRefreshKey,
    setSearchParams
}) => {

    const [timeRange, setTimeRange] = useState<{
        time_from: Date | null;
        time_to: Date | null;
    }>({
        time_from: parseUTC(filters.time_from),
        time_to: parseUTC(filters.time_to)
    });

    const filtersFormValue = useMemo(() => {
        return {
            time_range: timeRange.time_from && timeRange.time_to
                ? [timeRange.time_from, timeRange.time_to]
                : [],
            number_from: filters.number_from || "",
            number_to: filters.number_to || "",
            ip_src: filters.ip_src || "",
            ip_dst: filters.ip_dst || "",
            call_id: filters.call_id || ""
        };
    }, [filters]);

    return (
        <Panel
            expanded={expanded}
            noStyles
        >
            <Form
                model={model}
                formError={formError}
                formDefaultValue={filtersFormValue}
                onChange={debounce((value: any) => {
                    const [timeFrom, timeTo] = (value?.time_range as [Date, Date]) ?? [null, null];
                    
                    setTimeRange({ time_from: timeFrom ?? null, time_to: timeTo ?? null });

                    const filterValue: Partial<DumpFilters> = {
                        number_from: value.number_from || "",
                        number_to: value.number_to || "",
                        ip_src: value.ip_src || "",
                        ip_dst: value.ip_dst || "",
                        call_id: value.call_id || "",
                        time_from: timeFrom ? displayLocalDateToBackendUTCString(timeFrom) : "",
                        time_to: timeTo ? displayLocalDateToBackendUTCString(timeTo) : ""
                    };

                    const validation = model.check(filterValue as DumpFilters);
                    const newErrors: Record<string, string> = {};

                    Object.entries(validation).forEach(([key, val]: any) => {
                        if (val.hasError && !notUpdatableKeys.includes(key)) {
                            newErrors[key] = val.errorMessage;
                        }
                    });

                    setFormError(newErrors);

                    if (validationHasErrors(validation)) {
                        return;
                    }

                    setFilters((prev: any) => ({ ...prev, ...filterValue }));

                    const updatedParams = new URLSearchParams(currentParamsRef.current.toString());

                    Object.entries(filterValue).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && !notUpdatableKeys.includes(key)) {
                            updatedParams.set(key, value);
                        }
                    });

                    currentParamsRef.current = updatedParams;
                    setSearchParams(updatedParams);

                    setRefreshKey(Date.now());
                }, 500)}
            >
                <HStack spacing={16} justifyContent="space-between" alignItems="center">
                    <FormGroup gap={16} horizontal>
                        <FormControl
                            disabled={loading}
                            as={DateRangePicker}
                            width={330}
                            // defaultCalendarValue={[todayFrom, todayTo]}
                            value={
                                timeRange.time_from && timeRange.time_to
                                    ? [timeRange.time_from, timeRange.time_to]
                                    : null
                            }
                            onChange={(val: [Date, Date] | null) => {
                                let timeFrom = val?.[0] ?? null;
                                let timeTo = val?.[1] ?? null;

                                setTimeRange({ time_from: timeFrom, time_to: timeTo });

                                // const nextFilters = {
                                //     time_from: timeFrom ? displayLocalDateToBackendUTCString(timeFrom) : "",
                                //     time_to: timeTo ? displayLocalDateToBackendUTCString(timeTo) : ""
                                // };

                                // setFilters(prev => ({
                                //     ...prev,
                                //     ...nextFilters
                                // }));
                            }}
                            onClean={() => {
                                setTimeRange({ time_from: null, time_to: null });
                            }}
                            showOneCalendar
                            size="md"
                            name="time_range"
                            placeholder="Select range"
                            format="dd/MM/yyyy HH:mm:ss"
                        />
                        <FormControl
                            disabled={loading}
                            width={180}
                            as={Input}
                            // value={number}
                            // onChange={(val: any) => { setNumber(val); }}
                            placeholder="Calling number..."
                            name="number_from"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={180}
                            as={Input}
                            // value={number}
                            // onChange={(val: any) => { setNumber(val); }}
                            placeholder="Called number..."
                            name="number_to"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            // value={number}
                            // onChange={(val: any) => { setNumber(val); }}
                            placeholder="Incoming IP..."
                            name="ip_src"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            // value={number}
                            // onChange={(val: any) => { setNumber(val); }}
                            placeholder="Outgoing IP..."
                            name="ip_dst"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={300}
                            as={Input}
                            placeholder="Call ID..."
                            name="call_id"
                            size="md"
                        />
                    </FormGroup>
                </HStack>
            </Form>
        </Panel>
    );
};

export default ExtractedDumpsFilters; 